import { useCallback, useEffect } from "react";
import { useToast } from "@/hooks/use-toast";
import { PageTitle } from "@/components/ui/page-title";
import { useDocumentPreview } from "@/components/chat/SidebarChat/DocLink/useDocumentPreview";
import { DocumentViewerDialog } from "@/domains/documents/components/DocumentViewerDialog";
import type { BuildingHeaderAttributes } from "@/hooks/buildings/useBuildingAttributes";
import type { Translations } from "@/lib/i18n/types";
import { MetadataRow } from "./header/MetadataRow";

interface BuildingHeaderProps {
  building: {
    id: number | string;
    name: string;
    address: string;
    registry_code?: string | null;
    org_id?: string | null;
  };
  attributes: BuildingHeaderAttributes | undefined;
  onFieldsSave: (fields: Record<string, string>) => Promise<void>;
  t: Translations;
}

export function BuildingHeader({ building, attributes, onFieldsSave, t }: BuildingHeaderProps) {
  const { toast } = useToast();
  const documentPreview = useDocumentPreview(String(building.id));
  const { error: previewError, clearError: clearPreviewError } = documentPreview;

  useEffect(() => {
    if (previewError) {
      toast({
        title: "Cannot open document",
        description: previewError,
        variant: "destructive",
      });
      clearPreviewError();
    }
  }, [previewError, clearPreviewError, toast]);

  const handleSourceClick = useCallback(
    (filename: string) => {
      documentPreview.openPreview(filename);
    },
    [documentPreview]
  );

  return (
    <div>
      {/*
       * Top row: the static page title. The name and address live in the
       * BuildingContextLine above the page (rendered once by BuildingLayout),
       * so this header shows the page name only. Editing is per-field: the
       * metadata row below stays static text and clicking a value opens a
       * small edit popover anchored under it.
       */}
      <div className="min-w-0">
        <PageTitle info="building" className="leading-tight">
          {t.buildings.sidebar.building}
        </PageTitle>
      </div>

      <MetadataRow
        registryCode={building.registry_code ?? ""}
        attributes={attributes}
        onFieldsSave={onFieldsSave}
        onSourceClick={handleSourceClick}
        t={t}
      />

      <DocumentViewerDialog
        document={documentPreview.document}
        blobUrl={documentPreview.blobUrl}
        blobData={documentPreview.blobData}
        open={documentPreview.isOpen}
        onOpenChange={(open) => {
          if (!open) documentPreview.closePreview();
        }}
        originalDwgFilePath={documentPreview.originalDwgFilePath ?? undefined}
      />
    </div>
  );
}
